// An offline lot Save as the change queue holds it (BL-2431, ADR 066 — Offline Mode § 4.1): only
// the fields the operator changed, each with the value it had when the edit began, so the replay's
// conflict check refuses a field someone else changed meanwhile and never touches the rest.
import { fromWire, type MoneyWire } from '@auctogon/core/money';
import { LotPatchBaseValuesSchema } from '@auctogon/core-admin/schemas/lot-patch';

/** A Save's body or its baseline: field name → value, as the lot PATCH spells them. */
export interface LotPatchFields {
  [field: string]: unknown;
}

/** The body keys that are not lot fields: the lot id and the replay controls. */
const NOT_A_FIELD: ReadonlySet<string> = new Set(['id', 'baseValues', 'overwrite']);

/** The fields the PATCH takes a base for (the BL-2336 closed set). */
const BASE_KEYS: ReadonlySet<string> = new Set(Object.keys(LotPatchBaseValuesSchema.shape));

function isMoneyWire(value: unknown): value is MoneyWire {
  if (typeof value !== 'object' || value === null || !('amount' in value) || !('currency' in value)) return false;
  return typeof value.amount === 'string' && typeof value.currency === 'string' && Object.keys(value).length === 2;
}

/** An empty text and an absent value are the same "unset", as the server's check reads them. */
function unset(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/** JSON with object keys in a fixed order, so two equal objects compare equal. */
function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, v: unknown) =>
    v !== null && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(Object.entries(v).sort(([a], [b]) => a.localeCompare(b)))
      : v,
  );
}

/**
 * @param a - one field value.
 * @param b - another.
 * @returns whether a Save carrying `a` changes a field holding `b`: money compares as `Money`
 *   (`2.00` equals `2.0000`), an empty text equals unset, everything else as canonical JSON.
 */
export function lotPatchValuesEqual(a: unknown, b: unknown): boolean {
  if (unset(a) || unset(b)) return unset(a) && unset(b);
  if (isMoneyWire(a) && isMoneyWire(b)) {
    if (a.currency !== b.currency) return false;
    return fromWire(a).equals(fromWire(b));
  }
  return canonical(a) === canonical(b);
}

/**
 * The stored custom-field values for the slots a Save sends. The form sends only the house's
 * enabled slots, so a value the lot keeps for a disabled slot is not something the Save changes.
 */
function sentSlotsOf(sent: unknown, stored: unknown): unknown {
  if (typeof sent !== 'object' || sent === null || typeof stored !== 'object' || stored === null) return stored;
  const values = new Map(Object.entries(stored));
  return Object.fromEntries(Object.keys(sent).flatMap((slot) => (values.has(slot) ? [[slot, values.get(slot)]] : [])));
}

/** What {@link diffLotPatch} returns. */
export interface LotPatchDiff {
  /** Only the fields this Save changes, with their new values. */
  changes: LotPatchFields;
  /** Each changed field's value when the edit began, for the fields the PATCH takes a base for. */
  baseValues: LotPatchFields;
}

/**
 * The fields an offline Save changes. The online form sends every field, so a queued Save would
 * otherwise write fields the operator never touched over someone else's newer values.
 *
 * @param input.body - the Save's PATCH body, as the form builds it.
 * @param input.baseline - the same builder's body over the lot as it read when the edit began.
 * @returns the changed fields and their bases.
 */
export function diffLotPatch(input: { body: LotPatchFields; baseline: LotPatchFields }): LotPatchDiff {
  const changes: LotPatchFields = {};
  const baseValues: LotPatchFields = {};
  for (const [field, value] of Object.entries(input.body)) {
    if (NOT_A_FIELD.has(field) || value === undefined) continue;
    const base = field === 'customFields' ? sentSlotsOf(value, input.baseline[field]) : input.baseline[field];
    if (lotPatchValuesEqual(value, base)) continue;
    changes[field] = value;
    if (BASE_KEYS.has(field)) baseValues[field] = base ?? null;
  }
  return { changes, baseValues };
}
